import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Collapse,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { PageHeader } from '@opengov/components-page-header';
import { Magnify } from '@opengov/react-capital-assets';

import { SectionCard } from '../components/vcm/SectionCard';
import { useVcm } from '../contexts/VcmContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { CALL_REASONS } from '../lib/vcm/constants';
import { TEXAS_COUNTIES } from '../lib/vcm/counties';
import {
  mockSearchCallers,
  mockGetClientById,
  mockGetCallLog,
  mockGetCallLogByClientId,
  mockGetCasesByClientId,
  mockGetResources,
  mockAddReferral,
} from '../mocks/vcm-mock-data';
import type { TcrClient, TcrCase, CallLogEntry, TcrResource } from '../types/vcm';

function getInitials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function CallCenterPage() {
  const { currentUser } = useVcm();
  const { t } = useLanguage();
  const { navigateToPath } = useAppNavigation();

  // Lookup state
  const [lookupQuery, setLookupQuery] = useState('');
  const [selectedCaller, setSelectedCaller] = useState<TcrClient | null>(null);

  // Call form state
  const [callerFirst, setCallerFirst] = useState('');
  const [callerLast, setCallerLast] = useState('');
  const [callerPhone, setCallerPhone] = useState('');
  const [callerEmail, setCallerEmail] = useState('');
  const [callerCounty, setCallerCounty] = useState('');
  const [callerAddress, setCallerAddress] = useState('');
  const [callerAddress2, setCallerAddress2] = useState('');
  const [callerCity, setCallerCity] = useState('');
  const [callerZip, setCallerZip] = useState('');
  const [reason, setReason] = useState<string>(CALL_REASONS[0]);
  const [notes, setNotes] = useState('');
  const [referralId, setReferralId] = useState('');
  const [callerCases, setCallerCases] = useState<TcrCase[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string>('');
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [resourceDrawerOpen, setResourceDrawerOpen] = useState(false);

  // Call log state
  const [callLog, setCallLog] = useState<CallLogEntry[]>(() => mockGetCallLog());
  const [logSearch, setLogSearch] = useState('');
  const [detailEntry, setDetailEntry] = useState<CallLogEntry | null>(null);

  // Resources for referral browser
  const resources = useMemo(() => mockGetResources(), []);

  // Lookup results
  const lookupResults = useMemo(() => {
    if (lookupQuery.trim().length < 2) return [];
    return mockSearchCallers(lookupQuery);
  }, [lookupQuery]);

  // Inline duplicate detection from form fields (only when no caller is selected)
  // Requires first+last name together, or phone (7+ digits), or email (5+ chars)
  const formDuplicates = useMemo(() => {
    if (selectedCaller) return [];
    const seen = new Map<string, TcrClient>();
    // Name match only fires when both first AND last are provided
    const first = callerFirst.trim();
    const last = callerLast.trim();
    if (first.length >= 2 && last.length >= 2) {
      for (const c of mockSearchCallers(`${first} ${last}`)) seen.set(c.id, c);
    }
    // Phone is a strong signal on its own
    const rawPhone = callerPhone.replace(/\D/g, '');
    if (rawPhone.length >= 7) {
      for (const c of mockSearchCallers(rawPhone)) seen.set(c.id, c);
    }
    // Email is a strong signal on its own
    if (callerEmail.trim().length >= 5 && callerEmail.includes('@')) {
      for (const c of mockSearchCallers(callerEmail.trim())) seen.set(c.id, c);
    }
    // Address match — street number + street name is a strong signal
    if (callerAddress.trim().length >= 5) {
      for (const c of mockSearchCallers(callerAddress.trim())) seen.set(c.id, c);
    }
    return Array.from(seen.values());
  }, [selectedCaller, callerFirst, callerLast, callerPhone, callerEmail, callerAddress]);

  // Call history for selected caller
  const selectedCallerHistory = useMemo(() => {
    if (!selectedCaller) return [];
    return mockGetCallLogByClientId(selectedCaller.id);
  }, [selectedCaller]);

  // Filtered call log
  const filteredLog = useMemo(() => {
    if (!logSearch.trim()) return callLog;
    const q = logSearch.toLowerCase();
    return callLog.filter((c) =>
      `${c.callerName} ${c.callerPhone} ${c.reasonForCall} ${c.handledBy} ${c.outcome}`.toLowerCase().includes(q),
    );
  }, [callLog, logSearch]);

  function selectCaller(client: TcrClient) {
    setSelectedCaller(client);
    setCallerFirst(client.firstName);
    setCallerLast(client.lastName);
    setCallerPhone(client.phoneHome ? formatPhone(client.phoneHome) : '');
    setCallerEmail(client.email ?? '');
    setCallerCounty(client.county ?? '');
    setCallerAddress(client.primaryAddress?.address1 ?? '');
    setCallerAddress2(client.primaryAddress?.address2 ?? '');
    setCallerCity(client.primaryAddress?.city ?? '');
    setCallerZip(client.primaryAddress?.zip ?? '');
    const cases = client.contactOnly ? [] : mockGetCasesByClientId(client.id);
    setCallerCases(cases);
    setSelectedCaseId(cases.length === 1 ? (cases[0]?.caseId ?? '') : '');
    setLookupQuery('');
  }

  function clearSelection() {
    setSelectedCaller(null);
    setCallerFirst('');
    setCallerLast('');
    setCallerPhone('');
    setCallerEmail('');
    setCallerCounty('');
    setCallerAddress('');
    setCallerAddress2('');
    setCallerCity('');
    setCallerZip('');
    setCallerCases([]);
    setSelectedCaseId('');
    setFollowUpNeeded(false);
    setNotes('');
    setReason(CALL_REASONS[0]);
    setReferralId('');
  }

  function saveContactOnly() {
    if (!callerFirst.trim() || !callerLast.trim()) return;
    const fullName = `${callerFirst.trim()} ${callerLast.trim()}`;
    const resource = referralId ? resources.find((r) => r.id === referralId) : null;
    const linkedCase = selectedCaseId ? callerCases.find((c) => c.caseId === selectedCaseId) : null;
    const entry: CallLogEntry = {
      id: `call-${Date.now()}`,
      clientId: selectedCaller?.id ?? `CALL-NEW-${Date.now()}`,
      caseId: selectedCaseId || null,
      caseName: linkedCase ? `${linkedCase.caseId} — ${linkedCase.event}` : null,
      callerName: fullName,
      callerPhone: callerPhone.trim(),
      callerEmail: callerEmail.trim() || null,
      reasonForCall: reason,
      notes: notes.trim(),
      referralId: referralId || null,
      referralName: resource?.name ?? null,
      handledBy: currentUser.name,
      outcome: referralId ? 'Referred' : 'Contact Only',
      followUpNeeded,
      createdAt: new Date().toISOString(),
    };
    setCallLog((prev) => [entry, ...prev]);

    // Create a referral on the case's referrals tab if a resource + case are selected
    if (resource && selectedCaseId && selectedCaller) {
      const now = new Date().toISOString();
      mockAddReferral({
        id: `ref-cc-${Date.now()}`,
        caseId: selectedCaseId,
        clientId: selectedCaller.id,
        interactionId: entry.id,
        serviceName: resource.service ?? resource.name,
        agencyName: resource.organization,
        status: 'Referred',
        units: null,
        costPerUnit: null,
        amount: null,
        fundingAmount: null,
        notes: `Referral made during call center interaction by ${currentUser.name}.`,
        requestedDate: now.slice(0, 10),
        referredDate: now.slice(0, 10),
        acceptedDate: null,
        providedDate: null,
        declinedDate: null,
        unreachedDate: null,
        createdAt: now,
        updatedAt: now,
      });
    }

    clearSelection();
  }

  function buildPrefillParams(client?: TcrClient | null): string {
    const p = new URLSearchParams();
    if (client) {
      p.set('firstName', client.firstName);
      p.set('lastName', client.lastName);
      if (client.phoneHome) p.set('phone', client.phoneHome);
      if (client.email) p.set('email', client.email);
      if (client.dateOfBirth) p.set('dob', client.dateOfBirth);
      if (client.gender) p.set('gender', client.gender);
      if (client.preferredLanguage) p.set('language', client.preferredLanguage);
      if (client.primaryAddress?.address1) p.set('address', client.primaryAddress.address1);
      if (client.primaryAddress?.city) p.set('city', client.primaryAddress.city);
      if (client.primaryAddress?.zip) p.set('zip', client.primaryAddress.zip);
      p.set('source', 'call-center');
      p.set('sourceId', client.id);
    } else {
      if (callerFirst.trim()) p.set('firstName', callerFirst.trim());
      if (callerLast.trim()) p.set('lastName', callerLast.trim());
      if (callerPhone.trim()) p.set('phone', callerPhone.trim());
      if (callerEmail.trim()) p.set('email', callerEmail.trim());
      if (callerAddress.trim()) p.set('address', callerAddress.trim());
      if (callerAddress2.trim()) p.set('address2', callerAddress2.trim());
      if (callerCity.trim()) p.set('city', callerCity.trim());
      if (callerCounty.trim()) p.set('county', callerCounty.trim());
      if (callerZip.trim()) p.set('zip', callerZip.trim());
      p.set('source', 'call-center');
    }
    return p.toString();
  }

  function handleRegister() {
    navigateToPath(`clients/new?${buildPrefillParams(selectedCaller)}`);
  }

  function convertToCase(client: TcrClient) {
    navigateToPath(`clients/new?${buildPrefillParams(client)}`);
  }

  return (
    <Box sx={{ p: (t) => t.spacing(3), display: 'flex', flexDirection: 'column', gap: (t) => t.spacing(2) }}>
      <PageHeader
        title={t('callCenter')}
        description={t('callCenter')}
      />

      <Grid container spacing={2}>
        {/* Left column: Lookup + Call Form */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={2}>
            {/* Zone 1: Caller Lookup */}
            <Card variant="outlined">
              <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  Caller Lookup
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('searchCallerPlaceholder')}
                  value={lookupQuery}
                  onChange={(e) => setLookupQuery(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Box sx={{ display: 'flex', mr: 1, color: (t) => t.palette.text.secondary, '& svg': { width: 18, height: 18 } }}>
                          <Magnify />
                        </Box>
                      ),
                    },
                  }}
                />
                {/* Lookup results */}
                {lookupResults.length > 0 && (
                  <Stack spacing={1} sx={{ mt: 1.5 }}>
                    {lookupResults.map((client) => {
                      const history = mockGetCallLogByClientId(client.id);
                      return (
                        <Stack
                          key={client.id}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ width: 32, height: 32, bgcolor: client.contactOnly ? 'warning.main' : 'primary.main', fontSize: 12 }}>
                              {getInitials(client.firstName, client.lastName)}
                            </Avatar>
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{client.firstName} {client.lastName}</Typography>
                                <Chip label={client.contactOnly ? 'Contact Only' : 'Registered'} size="small" color={client.contactOnly ? 'warning' : 'success'} />
                              </Stack>
                              <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                                {client.phoneHome ?? '—'} · {client.email ?? '—'}
                                {history.length > 0 && ` · ${history.length} previous call${history.length > 1 ? 's' : ''}`}
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="outlined" onClick={() => selectCaller(client)}>{t('select')}</Button>
                            {client.contactOnly && (
                              <Button size="small" variant="contained" onClick={() => convertToCase(client)}>{t('convertToCase')}</Button>
                            )}
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
                {lookupQuery.trim().length >= 2 && lookupResults.length === 0 && (
                  <Typography variant="body2" sx={{ mt: 1.5, color: (t) => t.palette.text.secondary }}>
                    No match found — fill in the form below for a new caller.
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Zone 2: Log Call */}
            <Card variant="outlined">
              <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2">
                    {selectedCaller ? `Log Call — ${selectedCaller.firstName} ${selectedCaller.lastName}` : 'Log New Call'}
                  </Typography>
                  {selectedCaller && (
                    <Button size="small" variant="text" onClick={clearSelection}>{t('clear')}</Button>
                  )}
                </Stack>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label={t('firstName')} size="small" fullWidth required value={callerFirst} onChange={(e) => setCallerFirst(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label={t('lastName')} size="small" fullWidth required value={callerLast} onChange={(e) => setCallerLast(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label={t('phone')} size="small" fullWidth value={callerPhone} onChange={(e) => setCallerPhone(formatPhone(e.target.value))} placeholder="(512) 555-0134" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label={t('email')} size="small" fullWidth value={callerEmail} onChange={(e) => setCallerEmail(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField label={t('address')} size="small" fullWidth value={callerAddress} onChange={(e) => setCallerAddress(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <TextField label="Unit / Suite" size="small" fullWidth value={callerAddress2} onChange={(e) => setCallerAddress2(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label={t('city')} size="small" fullWidth value={callerCity} onChange={(e) => setCallerCity(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label={t('zip')} size="small" fullWidth value={callerZip} onChange={(e) => setCallerZip(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Autocomplete
                      size="small"
                      options={TEXAS_COUNTIES as unknown as string[]}
                      value={callerCounty || null}
                      onChange={(_e, v) => setCallerCounty(v ?? '')}
                      renderInput={(params) => <TextField {...params} label={t('county')} />}
                      renderOption={(props, option) => (
                        <li {...props} key={option} style={{ color: '#000' }}>{option}</li>
                      )}
                    />
                  </Grid>
                  {callerCases.length > 0 && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        select
                        label={t('linkToCase')}
                        size="small"
                        fullWidth
                        value={selectedCaseId}
                        onChange={(e) => setSelectedCaseId(e.target.value)}
                        required
                      >
                        {callerCases.map((c) => (
                          <MenuItem key={c.caseId} value={c.caseId}>
                            {c.caseId} — {c.event} ({c.status})
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: callerCases.length > 0 ? 6 : 6 }}>
                    <TextField select label={t('reasonForCall')} size="small" fullWidth value={reason} onChange={(e) => setReason(e.target.value)}>
                      {CALL_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </TextField>
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <Button size="small" variant="outlined" onClick={() => setResourceDrawerOpen(true)}>
                    Browse Referrals
                  </Button>
                  <FormControlLabel
                    control={<Checkbox checked={followUpNeeded} onChange={(e) => setFollowUpNeeded(e.target.checked)} />}
                    label="Follow Up Needed"
                    sx={{ '& .MuiTypography-root': { fontSize: 13 } }}
                  />
                </Stack>
                <Box sx={{ mt: 2, width: '100%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: (t) => t.palette.text.secondary, display: 'block', mb: 0.5 }}>{t('callNotes')}</Typography>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    placeholder="Describe the call, what was discussed, and any actions taken..."
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14, fontFamily: 'inherit', borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </Box>
                {/* Duplicate detection warning */}
                {formDuplicates.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Possible match found ({formDuplicates.length})
                    </Typography>
                    <Stack spacing={0.5}>
                      {formDuplicates.map((dup) => (
                        <Stack key={dup.id} direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption">
                            {dup.firstName} {dup.lastName} · {dup.phoneHome ?? 'No phone'} · {dup.county} County
                            {dup.contactOnly ? ' (Contact Only)' : ` · ${dup.openCases} case(s)`}
                          </Typography>
                          <Button size="small" variant="outlined" sx={{ minWidth: 60, py: 0 }} onClick={() => selectCaller(dup)}>
                            {t('select')}
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  </Alert>
                )}


                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
                  <Button variant="contained" onClick={saveContactOnly} disabled={!callerFirst.trim() || !callerLast.trim()}>
                    {t('saveContactOnly')}
                  </Button>
                  {selectedCaller && !selectedCaller.contactOnly ? (
                    <Button variant="outlined" onClick={() => navigateToPath(`clients/${selectedCaller.id}`)}>
                      {t('goToClientRecord')}
                    </Button>
                  ) : (
                    <Button variant="outlined" onClick={handleRegister} disabled={!callerFirst.trim() || !callerLast.trim()}>
                      {t('registerAsClient')}
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right column: Selected caller history */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                {selectedCaller ? `Call History — ${selectedCaller.firstName} ${selectedCaller.lastName}` : 'Call History'}
              </Typography>
              {selectedCaller ? (
                selectedCallerHistory.length > 0 ? (
                  <Stack spacing={1.5}>
                    {selectedCallerHistory.map((entry) => (
                      <Box key={entry.id} sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip label={entry.outcome} size="small" color={entry.outcome === 'Registered' ? 'success' : entry.outcome === 'Referred' ? 'info' : 'default'} />
                            <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                              {new Date(entry.createdAt).toLocaleDateString()} {new Date(entry.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>{entry.handledBy}</Typography>
                        </Stack>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 0.5 }}>{entry.reasonForCall}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{entry.notes}</Typography>
                        {entry.referralName && (
                          <Chip label={`Referred: ${entry.referralName}`} size="small" variant="outlined" color="info" sx={{ mt: 1 }} />
                        )}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: (th) => th.palette.text.secondary }}>{t('noPreviousCalls')}</Typography>
                )
              ) : (
                <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                  Search for a caller above to see their call history.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Zone 3: All Call Log */}
      <SectionCard title={`Recent Call Log (${filteredLog.length})`} disableContentPadding>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('searchCallerPlaceholder')}
            value={logSearch}
            onChange={(e) => setLogSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <Box sx={{ display: 'flex', mr: 1, color: (t) => t.palette.text.secondary, '& svg': { width: 18, height: 18 } }}>
                    <Magnify />
                  </Box>
                ),
              },
            }}
          />
        </Box>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: '10%' }}>{t('date')}</TableCell>
                <TableCell align="center" sx={{ width: '15%' }}>{t('callerName')}</TableCell>
                <TableCell align="center" sx={{ width: '12%' }}>{t('phone')}</TableCell>
                <TableCell align="center" sx={{ width: '14%' }}>{t('case_')}</TableCell>
                <TableCell align="center" sx={{ width: '17%' }}>{t('reasonForCall')}</TableCell>
                <TableCell align="center" sx={{ width: '12%' }}>{t('handledBy')}</TableCell>
                <TableCell align="center" sx={{ width: '12%' }}>{t('outcome')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLog.map((entry) => (
                <TableRow key={entry.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailEntry(entry)}>
                  <TableCell align="center">{new Date(entry.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.callerName}</Typography>
                  </TableCell>
                  <TableCell align="center">{entry.callerPhone}</TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">{entry.caseName ?? '—'}</Typography>
                  </TableCell>
                  <TableCell align="center">{entry.reasonForCall}</TableCell>
                  <TableCell align="center">{entry.handledBy}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.outcome}</Typography>
                  </TableCell>
                </TableRow>
              ))}
              {filteredLog.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: (t) => t.spacing(3) }}>
                    <Typography variant="body2" sx={{ color: (th) => th.palette.text.secondary }}>{t('noCallLogEntries')}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {/* Call detail dialog — shows caller info + full note history */}
      <Dialog open={detailEntry !== null} onClose={() => setDetailEntry(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Call Record — {detailEntry?.callerName}
          <Typography variant="caption" sx={{ display: 'block', color: (t) => t.palette.text.secondary }}>
            {detailEntry?.callerPhone} {detailEntry?.callerEmail ? `· ${detailEntry.callerEmail}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detailEntry && (() => {
            const allCalls = callLog
              .filter((c) => c.clientId === detailEntry.clientId)
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            return (
              <Stack spacing={2}>
                {/* Caller summary */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="overline" sx={{ fontSize: 10, color: (t) => t.palette.text.secondary }}>Total Calls</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{allCalls.length}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="overline" sx={{ fontSize: 10, color: (t) => t.palette.text.secondary }}>First Contact</Typography>
                    <Typography variant="body2">{allCalls.length > 0 ? new Date(allCalls.at(-1)?.createdAt ?? "").toLocaleDateString() : '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="overline" sx={{ fontSize: 10, color: (t) => t.palette.text.secondary }}>Last Contact</Typography>
                    <Typography variant="body2">{allCalls.length > 0 ? new Date(allCalls.at(0)?.createdAt ?? "").toLocaleDateString() : '—'}</Typography>
                  </Grid>
                </Grid>

                {/* Notes timeline — all calls, newest first */}
                <Box>
                  <Typography variant="subtitle2" sx={{ pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>
                    Call Notes ({allCalls.length})
                  </Typography>
                  <Stack spacing={1.5}>
                    {allCalls.map((call) => (
                      <Box key={call.id} sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: call.id === detailEntry.id ? 'primary.main' : 'divider', bgcolor: call.id === detailEntry.id ? 'primary.lighter' : undefined }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {new Date(call.createdAt).toLocaleDateString()} {new Date(call.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                            </Typography>
                            <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>— {call.handledBy}</Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5}>
                            <Chip label={call.outcome} size="small" color={call.outcome === 'Registered' ? 'success' : call.outcome === 'Referred' ? 'info' : 'default'} />
                          </Stack>
                        </Stack>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 0.5, color: (t) => t.palette.text.secondary }}>{call.reasonForCall}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{call.notes || 'No notes.'}</Typography>
                        {call.referralName && (
                          <Chip label={`Referred: ${call.referralName}`} size="small" variant="outlined" color="info" sx={{ mt: 1 }} />
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailEntry(null)}>Close</Button>
          {detailEntry && (
            <Button variant="outlined" onClick={() => {
              const client = mockGetClientById(detailEntry.clientId);
              if (client) { selectCaller(client); setDetailEntry(null); }
            }}>
              Load Caller
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Resource Browser Drawer */}
      <ResourceBrowserDrawer
        open={resourceDrawerOpen}
        onClose={() => setResourceDrawerOpen(false)}
        onSelect={(resource) => {
          setReferralId(resource.id);
          const referralNote = `\n\n--- Referral: ${resource.name} (${resource.organization}) ---\nCategory: ${resource.category}\nService: ${resource.service}\nCounty: ${resource.county}${resource.contactInfo ? `\nContact: ${resource.contactInfo}` : ''}${resource.eligibilityNotes ? `\nEligibility: ${resource.eligibilityNotes}` : ''}`;
          setNotes((prev) => prev.trimEnd() + referralNote);
          setResourceDrawerOpen(false);
        }}
        resources={resources}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Resource Browser Drawer — browsable resource library in a side panel
// ---------------------------------------------------------------------------

const STATUS_COLOR: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  Active: 'success',
  Inactive: 'error',
  'Under Review': 'warning',
};

function ResourceBrowserDrawer({
  open,
  onClose,
  onSelect,
  resources,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (resource: TcrResource) => void;
  resources: TcrResource[];
}) {
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [countyFilter, setCountyFilter] = useState('All');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const categories = useMemo(() => ['All', ...new Set(resources.map((r) => r.category))], [resources]);
  const counties = useMemo(() => ['All', ...new Set(resources.map((r) => r.county))], [resources]);

  const filtered = useMemo(() => {
    let results = resources.filter((r) => r.status === 'Active');
    if (query.trim()) {
      const q = query.toLowerCase();
      results = results.filter((r) =>
        `${r.name} ${r.category} ${r.service} ${r.organization}`.toLowerCase().includes(q),
      );
    }
    if (categoryFilter !== 'All') results = results.filter((r) => r.category === categoryFilter);
    if (countyFilter !== 'All') results = results.filter((r) => r.county === countyFilter);
    return results;
  }, [resources, query, categoryFilter, countyFilter]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: '45%', md: '40%' }, p: 2.5 } }}>
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Browse Resources</Typography>
          <Button size="small" onClick={onClose}>Close</Button>
        </Stack>

        <TextField
          fullWidth
          size="small"
          placeholder="Search resources..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          slotProps={{ input: { startAdornment: <Box sx={{ display: 'flex', mr: 1, color: 'text.secondary', '& svg': { width: 18, height: 18 } }}><Magnify /></Box> } }}
        />

        <Stack direction="row" spacing={1}>
          <TextField select size="small" label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} sx={{ flex: 1 }}>
            {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="County" value={countyFilter} onChange={(e) => setCountyFilter(e.target.value)} sx={{ flex: 1 }}>
            {counties.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Stack>

        <Divider />

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{filtered.length} resources</Typography>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Stack spacing={1.5}>
            {filtered.map((resource) => {
              const isExpanded = expandedId === resource.id;
              return (
                <Card
                  key={resource.id}
                  variant="outlined"
                  sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' }, borderColor: isExpanded ? 'primary.main' : undefined }}
                  onClick={() => setExpandedId(isExpanded ? null : resource.id)}
                >
                  <CardContent sx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{resource.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{resource.organization} · {resource.county}</Typography>
                      </Box>
                      <Chip label={resource.status} size="small" variant="outlined" color={STATUS_COLOR[resource.status] ?? 'default'} sx={{ flexShrink: 0 }} />
                    </Stack>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Service</Typography>
                          <Typography variant="body2">{resource.service}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Category</Typography>
                          <Typography variant="body2">{resource.category}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Geographic Area</Typography>
                          <Typography variant="body2">{resource.geographicArea ?? '—'}</Typography>
                        </Box>
                        {resource.contactInfo && (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Contact</Typography>
                            <Typography variant="body2">{resource.contactInfo}</Typography>
                          </Box>
                        )}
                        {resource.eligibilityNotes && (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Eligibility Notes</Typography>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{resource.eligibilityNotes}</Typography>
                          </Box>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                          onClick={(e) => { e.stopPropagation(); onSelect(resource); }}
                        >
                          Select this resource
                        </Button>
                      </Stack>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
            {filtered.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                No resources match your search.
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
}
